import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useLanguage, pick } from "@/lib/lang";
import { t } from "@/lib/content";
import { TESTIDS } from "@/constants/testIds";
import { PORTRAITS } from "@/lib/portraits";
import Reveal from "@/components/ui-brand/Reveal";
import PeonyMark from "@/components/ui-brand/PeonyMark";
import PhotoPlaceholder from "@/components/ui-brand/PhotoPlaceholder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const { lang } = useLanguage();
  const location = useLocation();
  const bookingRef = useRef(null);

  const [form, setForm] = useState({ name: "", email: "", topic: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [msg, setMsg] = useState("");

  const [bookingOpen, setBookingOpen] = useState(false);
  const [booking, setBooking] = useState({
    name: "",
    email: "",
    phone: "",
    project_type: "",
    preferred_date: "",
    notes: "",
  });
  const [bookingStatus, setBookingStatus] = useState("idle");
  const [bookingMsg, setBookingMsg] = useState("");

  useEffect(() => {
    if (location.hash === "#termin") {
      setBookingOpen(true);
      setTimeout(() => bookingRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    }
  }, [location.hash]);

  const submitContact = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, { ...form, language: lang, source: "patrice.studio" });
      setStatus("success");
      setMsg(pick(t.contact.form.success, lang));
      setForm({ name: "", email: "", topic: "", message: "" });
    } catch (err) {
      setStatus("error");
      setMsg(pick(t.contact.form.error, lang));
    }
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    setBookingStatus("sending");
    try {
      await axios.post(`${API}/booking`, { ...booking, language: lang });
      setBookingStatus("success");
      setBookingMsg(pick(t.contact.booking_form.success, lang));
      setBooking({ name: "", email: "", phone: "", project_type: "", preferred_date: "", notes: "" });
    } catch (err) {
      setBookingStatus("error");
      setBookingMsg(pick(t.contact.form.error, lang));
    }
  };

  return (
    <>
      {/* HERO */}
      <section
        data-testid={TESTIDS.contactHero}
        className="relative px-6 md:px-12 lg:px-24 pt-6 md:pt-12 pb-12 md:pb-20 overflow-hidden"
      >
        <PeonyMark size={680} opacity={0.05} className="absolute -left-32 -top-20 pointer-events-none" />
        <div className="grid grid-cols-12 gap-6 lg:gap-12 items-end">
          <div className="col-span-12 lg:col-span-8">
            <Reveal>
              <p className="eyebrow">{pick(t.contact.eyebrow, lang)}</p>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="headline-hero mt-10">
                <span className="italic-serif text-atmosphere">{pick(t.contact.title, lang).split(" ")[0]}</span>{" "}
                {pick(t.contact.title, lang).split(" ").slice(1).join(" ")}
              </h1>
            </Reveal>
            <Reveal delay={260}>
              <p className="body-lux-lg max-w-2xl mt-10">{pick(t.contact.lede, lang)}</p>
            </Reveal>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <Reveal delay={200}>
              <div className="grid grid-cols-12 gap-5 items-end">
                <div className="col-span-7">
                  <PhotoPlaceholder
                    testId="contact-portrait"
                    src={PORTRAITS.contactSide.src}
                    alt={PORTRAITS.contactSide.alt}
                    caption={PORTRAITS.contactSide.caption}
                    label={PORTRAITS.contactSide.label}
                    aspect={PORTRAITS.contactSide.aspect}
                    tone={PORTRAITS.contactSide.tone}
                  />
                </div>
                <div className="col-span-5">
                  <p className="font-signature text-4xl text-atmosphere leading-none">Patrice</p>
                  <p className="font-mono text-[0.62rem] tracking-[0.32em] uppercase mt-4 text-atmosphere">
                    {lang === "de" ? "antwortet meist innerhalb von 24 h" : "usually replies within 24 h"}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SPLIT — left details, right form */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-24">
        <div className="grid grid-cols-12 gap-10 lg:gap-16">
          {/* Left – elsewhere & booking CTA */}
          <div className="col-span-12 lg:col-span-5">
            <Reveal>
              <div ref={bookingRef} id="termin" className="border border-warmth/50 p-8 md:p-10 relative overflow-hidden">
                <PeonyMark size={260} opacity={0.08} className="absolute -right-10 -bottom-10" />
                <span className="eyebrow">— {pick(t.contact.booking_block.label, lang)}</span>
                <h3 className="font-display italic text-3xl md:text-4xl mt-4 text-depth leading-tight">
                  {pick(t.contact.booking_block.title, lang)}
                </h3>
                <p className="body-lux mt-6">{pick(t.contact.booking_block.body, lang)}</p>
                {!bookingOpen ? (
                  <button
                    type="button"
                    onClick={() => setBookingOpen(true)}
                    data-testid={TESTIDS.bookingOpen}
                    className="btn-primary mt-10"
                  >
                    {pick(t.contact.booking_block.cta, lang)} <span aria-hidden>↗</span>
                  </button>
                ) : (
                  <p className="mt-10 font-mono text-[0.7rem] tracking-[0.32em] uppercase text-signal">
                    ↓ {lang === "de" ? "Formular unten" : "Form below"}
                  </p>
                )}
              </div>
            </Reveal>

            <Reveal delay={160}>
              <div className="mt-12">
                <p className="eyebrow">— {pick(t.contact.elsewhere, lang)}</p>
                <ul className="mt-8 space-y-6">
                  {t.contact.socials.map((s) => (
                    <li key={s.label} className="border-b border-warmth/40 pb-5">
                      <a
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-baseline justify-between gap-3 group"
                      >
                        <span className="font-display text-2xl md:text-3xl text-depth group-hover:italic transition-all">
                          {s.label}
                        </span>
                        <span className="font-mono text-xs text-atmosphere">{s.handle} ↗</span>
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </div>

          {/* Right – contact form */}
          <div className="col-span-12 lg:col-span-7">
            <Reveal>
              <form
                onSubmit={submitContact}
                data-testid={TESTIDS.contactForm}
                className="grid grid-cols-12 gap-x-8 gap-y-10"
              >
                <div className="col-span-12 md:col-span-6 field">
                  <label className="field-label" htmlFor="c-name">
                    {pick(t.contact.form.name, lang)}
                  </label>
                  <input
                    id="c-name"
                    type="text"
                    required
                    data-testid={TESTIDS.contactName}
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder={pick(t.contact.form.placeholder_name, lang)}
                    className="field-input"
                  />
                </div>
                <div className="col-span-12 md:col-span-6 field">
                  <label className="field-label" htmlFor="c-email">
                    {pick(t.contact.form.email, lang)}
                  </label>
                  <input
                    id="c-email"
                    type="email"
                    required
                    data-testid={TESTIDS.contactEmail}
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    placeholder={pick(t.contact.form.placeholder_email, lang)}
                    className="field-input"
                  />
                </div>
                <div className="col-span-12 field">
                  <label className="field-label" htmlFor="c-topic">
                    {pick(t.contact.form.topic, lang)}
                  </label>
                  <input
                    id="c-topic"
                    type="text"
                    data-testid={TESTIDS.contactTopic}
                    value={form.topic}
                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    placeholder={pick(t.contact.form.placeholder_topic, lang)}
                    className="field-input"
                  />
                </div>
                <div className="col-span-12 field">
                  <label className="field-label" htmlFor="c-message">
                    {pick(t.contact.form.message, lang)}
                  </label>
                  <textarea
                    id="c-message"
                    required
                    data-testid={TESTIDS.contactMessage}
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    placeholder={pick(t.contact.form.placeholder_message, lang)}
                    className="field-textarea"
                  />
                </div>
                <div className="col-span-12 flex flex-wrap items-center gap-6 pt-4">
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid={TESTIDS.contactSubmit}
                    className="btn-primary disabled:opacity-50"
                  >
                    {status === "sending" ? "…" : pick(t.contact.form.submit, lang)} <span aria-hidden>↗</span>
                  </button>
                  {status === "success" && (
                    <p data-testid={TESTIDS.contactSuccess} className="font-display italic text-atmosphere">
                      {msg}
                    </p>
                  )}
                  {status === "error" && (
                    <p data-testid={TESTIDS.contactError} className="font-mono text-xs uppercase tracking-[0.25em] text-signal">
                      {msg}
                    </p>
                  )}
                </div>
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BOOKING FORM (expandable) */}
      {bookingOpen && (
        <section className="px-6 md:px-12 lg:px-24 py-16 md:py-24 bg-soft_canvas border-y border-warmth/40 relative overflow-hidden">
          <PeonyMark size={580} opacity={0.05} className="absolute -right-32 -top-32 pointer-events-none" />
          <Reveal>
            <div className="flex items-baseline justify-between gap-6 flex-wrap">
              <h2 className="headline-display">{pick(t.contact.booking_form.title, lang)}</h2>
              <button
                type="button"
                onClick={() => setBookingOpen(false)}
                data-testid={TESTIDS.bookingClose}
                className="font-mono text-[0.7rem] tracking-[0.32em] uppercase text-atmosphere hover:text-depth"
              >
                {lang === "de" ? "Schließen" : "Close"} ✕
              </button>
            </div>
          </Reveal>

          <Reveal delay={120}>
            <form
              onSubmit={submitBooking}
              data-testid={TESTIDS.bookingForm}
              className="mt-12 grid grid-cols-12 gap-x-8 gap-y-10"
            >
              <div className="col-span-12 md:col-span-6 field">
                <label className="field-label" htmlFor="b-name">{pick(t.contact.form.name, lang)}</label>
                <input
                  id="b-name"
                  type="text"
                  required
                  data-testid={TESTIDS.bookingName}
                  value={booking.name}
                  onChange={(e) => setBooking({ ...booking, name: e.target.value })}
                  className="field-input"
                  placeholder={pick(t.contact.form.placeholder_name, lang)}
                />
              </div>
              <div className="col-span-12 md:col-span-6 field">
                <label className="field-label" htmlFor="b-email">{pick(t.contact.form.email, lang)}</label>
                <input
                  id="b-email"
                  type="email"
                  required
                  data-testid={TESTIDS.bookingEmail}
                  value={booking.email}
                  onChange={(e) => setBooking({ ...booking, email: e.target.value })}
                  className="field-input"
                  placeholder={pick(t.contact.form.placeholder_email, lang)}
                />
              </div>
              <div className="col-span-12 md:col-span-6 field">
                <label className="field-label" htmlFor="b-phone">{pick(t.contact.booking_form.phone, lang)}</label>
                <input
                  id="b-phone"
                  type="tel"
                  data-testid={TESTIDS.bookingPhone}
                  value={booking.phone}
                  onChange={(e) => setBooking({ ...booking, phone: e.target.value })}
                  className="field-input"
                  placeholder="+49 …"
                />
              </div>
              <div className="col-span-12 md:col-span-6 field">
                <label className="field-label" htmlFor="b-date">{pick(t.contact.booking_form.date, lang)}</label>
                <input
                  id="b-date"
                  type="text"
                  data-testid={TESTIDS.bookingDate}
                  value={booking.preferred_date}
                  onChange={(e) => setBooking({ ...booking, preferred_date: e.target.value })}
                  className="field-input"
                  placeholder={lang === "de" ? "z. B. KW 12 · vormittags" : "e.g. week 12 · morning"}
                />
              </div>
              <div className="col-span-12 field">
                <label className="field-label" htmlFor="b-type">{pick(t.contact.booking_form.project_type, lang)}</label>
                <select
                  id="b-type"
                  required
                  data-testid={TESTIDS.bookingProjectType}
                  value={booking.project_type}
                  onChange={(e) => setBooking({ ...booking, project_type: e.target.value })}
                  className="field-input bg-transparent appearance-none cursor-pointer"
                >
                  <option value="">—</option>
                  {t.contact.booking_form.project_options.map((opt, i) => (
                    <option key={i} value={pick(opt, "de")}>{pick(opt, lang)}</option>
                  ))}
                </select>
              </div>
              <div className="col-span-12 field">
                <label className="field-label" htmlFor="b-notes">{pick(t.contact.booking_form.notes, lang)}</label>
                <textarea
                  id="b-notes"
                  rows={4}
                  data-testid={TESTIDS.bookingNotes}
                  value={booking.notes}
                  onChange={(e) => setBooking({ ...booking, notes: e.target.value })}
                  className="field-textarea"
                  placeholder={pick(t.contact.form.placeholder_message, lang)}
                />
              </div>
              <div className="col-span-12 flex flex-wrap items-center gap-6 pt-2">
                <button
                  type="submit"
                  disabled={bookingStatus === "sending"}
                  data-testid={TESTIDS.bookingSubmit}
                  className="btn-primary disabled:opacity-50"
                >
                  {bookingStatus === "sending" ? "…" : pick(t.contact.booking_form.submit, lang)} <span aria-hidden>↗</span>
                </button>
                {bookingStatus === "success" && (
                  <p data-testid={TESTIDS.bookingSuccess} className="font-display italic text-atmosphere">
                    {bookingMsg}
                  </p>
                )}
                {bookingStatus === "error" && (
                  <p className="font-mono text-xs uppercase tracking-[0.25em] text-signal">{bookingMsg}</p>
                )}
              </div>
            </form>
          </Reveal>
        </section>
      )}
    </>
  );
};

export default ContactPage;
